/**
 * Frontend internationalization (i18n) module
 *
 * Provides translation functionality for TypeScript pages and components.
 * Translations are loaded based on the locale set by the server via window.__LOCALE__.
 */

import en from '../i18n/en';
import de from '../i18n/de';
import fr from '../i18n/fr';
import it from '../i18n/it';

type Translations = Record<string, string>;

const translations: Record<string, Translations> = { en, de, fr, it };

/**
 * Get the current locale from the HTML lang attribute
 */
export function getLocale(): string {
    return document.documentElement.lang || 'en';
}

/**
 * Get a translated string
 *
 * @param key - The translation key (e.g., 'auth.sign_in')
 * @param params - Optional parameters to substitute (e.g., { name: 'John' })
 * @returns The translated string, or the key if not found
 *
 * @example
 * t('auth.sign_in') // "Sign in"
 * t('dashboard.welcome', { name: 'John' }) // "Welcome, John"
 */
export function t(key: string, params?: Record<string, string>): string {
    const locale = getLocale();
    const localeTranslations = translations[locale] || translations['en'];

    let text = localeTranslations[key];

    // Fall back to English if key not found in current locale
    if (text === undefined && locale !== 'en') {
        text = translations['en'][key];
    }

    // Return key if no translation found
    if (text === undefined) {
        return key;
    }

    // Replace parameters
    if (params) {
        for (const [name, value] of Object.entries(params)) {
            text = text.replace(`{${name}}`, value);
        }
    }

    return text;
}
